import { Horse as HorseIcon } from '@praetorian-chariot/ui';
import { ReactNode } from 'react';

import { cn } from '@/utils/classname';

export interface NoDataProps {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  styleType?: 'text' | 'textWithIcon';
  className?: string;
  action?: ReactNode;
}

/**
 * NoData component displays empty states with customizable icon and text.
 *
 * @remarks
 * This component is theme-aware and adapts to light/dark mode using Chariot design tokens.
 *
 * @param props.icon - Custom icon element (default: HorseIcon)
 * @param props.title - Main message text (required)
 * @param props.description - Optional secondary text or React element
 * @param props.styleType - Display variant: 'text' | 'textWithIcon' (default)
 * @param props.className - Additional Tailwind classes
 * @param props.action - Optional action element (button, link, etc.)
 *
 * @example
 * ```tsx
 * <NoData
 *   title="No Results Found"
 *   description="Try adjusting your filters"
 *   action={<Button label="Reset Filters" />}
 * />
 * ```
 */
export const NoData = ({
  icon = <HorseIcon />,
  title,
  description,
  styleType = 'textWithIcon',
  className,
  action,
}: NoDataProps) => {
  const isText = styleType === 'text';

  return (
    // Container query wrapper - establishes container context
    <div className="@container flex min-h-[200px] w-full flex-1">
      {/* Inner content - responds to container width (not viewport) */}
      <div
        data-testid="no-data"
        className={cn(
          // Base layout
          'flex w-full flex-col items-center justify-center gap-2 rounded-lg',
          // Container-responsive padding: compact (p-6) in narrow, spacious (p-12) in wide (768px+)
          'p-6 @3xl:p-12',
          // Background styling
          !isText &&
            'bg-chariot-background-secondary text-chariot-text-primary',
          isText && 'text-default-light gap-0 p-0',
          className
        )}
      >
        {!isText && (
          <div
            className={cn(
              'flex items-center justify-center',
              // Container-responsive icon sizing (raised breakpoints for drawers):
              // - Base (narrow containers): 48px (drawers < 768px)
              // - @xl (576px+): 64px (wide drawers)
              // - @2xl (672px+): 80px
              // - @3xl (768px+): 100px (main tables)
              // - @4xl (896px+): 120px
              // - @5xl (1024px+): 140px
              '[&>svg]:h-12 [&>svg]:w-auto',
              '@xl:[&>svg]:h-16',
              '@2xl:[&>svg]:h-20',
              '@3xl:[&>svg]:h-[100px]',
              '@4xl:[&>svg]:h-[120px]',
              '@5xl:[&>svg]:h-[140px]'
            )}
          >
            {icon}
          </div>
        )}
        {!isText && (
          <h6 className={cn('text-center font-bold', 'text-base @3xl:text-lg')}>
            {title}
          </h6>
        )}
        {isText && (
          <p className="text-center whitespace-break-spaces">{title}</p>
        )}
        {description && (
          <div className="text-chariot-text-secondary text-center text-sm whitespace-break-spaces">
            {description}
          </div>
        )}
        {action && <div className="mt-4">{action}</div>}
      </div>
    </div>
  );
};
